import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  Col,
  Container,
  Row,
  Spinner,
  Tab,
  Tabs,
} from "react-bootstrap";
import css from "../components/DocumentPanel/DocumentPanel.module.css";
import Tiptap from "../components/TipTap/TipTap";
import { useAuth } from "../../../../utils/hooks/useAuth";

import {
  billsSelector,
  fetchBill,
  fetchBills,
  saveDocumentState,
  setProposalStateSavingError,
} from "../_redux/billsSlice";

import {
  clearPublishableProposalId,
  deleteProposal,
  fetchProposals,
  proposalsStateSelector,
  publishProposal,
} from "../_redux/proposalsSlice";

import { fetchTeam, teamsSelector } from "../../Teams/_redux/teamsSlice";

import BillHeading from "../components/BillHeading/BillHeading";
import BillToggle from "../components/BillToggle/BillToggle";
import { FormattedMessage, useIntl } from "react-intl";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BillVoting from "../components/BillVoting/BillVoting";
import TeamEditModal from "../../Teams/components/team-edit-modal/TeamEditModal";
import ConfirmDeleteModal from "../components/confirm-delete-modal/ConfirmDeleteModal";
import { useFlags } from "../../../../config/featureFlags";
import ModalLayout from "../../../../_metronic/layout/components/modals/ModalLayout";
import { Permission } from "../../../../config/abac.config";
import { useAbac } from "react-abac";
import BillShowCode from "../components/BillShowCode/BillShowCode";
import BillExportToDocx from "../components/BillExportToDocx/BillExportToDocx";
import BillPopup from "../components/BillPopup";

interface ParamTypes {
  id: string;
  documentId?: string;
}

const BillPage = () => {
  const dispatch = useDispatch();
  const { userId, isModerator, isAdmin } = useAuth();
  const intl = useIntl();
  const { userHasPermissions } = useAbac();
  const { DISPLAY_LIKUMI_CONTENT, DISPLAY_CODE_CONTENT } = useFlags();

  const {
    fetchingCurrentBillsInProgress,
    currentBill,
    fetchingCurrentBillError,
    savingProposalStateError,
    savingVoteBillStateError,
  } = useSelector(billsSelector);

  const { deletableProposalId, publishableProposalId } = useSelector(
    proposalsStateSelector
  );

  const { currentTeam, teamInfoIsNotFilled } = useSelector(teamsSelector);

  let { id: billId } = useParams<ParamTypes>();

  const billHasPassedDeadline =
    currentBill?.deadline && new Date(currentBill.deadline) <= new Date();

  //I showing proposal 'home' in CODE project
  if (!billId && DISPLAY_CODE_CONTENT) billId = "34";
  //END. I showing proposal 'home' in CODE project

  const [, setCurrentTeamId] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [confirmModalShow, setConfirmModalShow] = useState(false);
  const [unAuthorized, setUnAuthorized] = useState(false);

  useEffect(() => {
    dispatch(fetchBill(billId));
    dispatch(fetchBills());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billId]);

  useEffect(() => {
    if (
      currentBill &&
      currentBill.documents &&
      currentBill.documents.length > 0
    ) {
      const documentIds = currentBill.documents.map((a) => a.id);
      const teamId = localStorage.getItem("teamId");
      setCurrentTeamId(parseInt(teamId));
      dispatch(fetchProposals({ documentIds, teamId: parseInt(teamId) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBill]);

  useEffect(() => {
    if (deletableProposalId) {
      setConfirmModalShow(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletableProposalId]);

  useEffect(() => {
    if (publishableProposalId) {
      handleCheckTeamInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishableProposalId]);

  useEffect(() => {
    if (savingProposalStateError?.statusCode === 401) {
      setUnAuthorized(true);
      dispatch(
        setProposalStateSavingError({
          message: undefined,
          statusCode: undefined,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savingProposalStateError]);

  useEffect(() => {
    toast.error(savingProposalStateError?.message);
  }, [savingProposalStateError]);

  useEffect(() => {
    if (savingVoteBillStateError) {
      toast.error(intl.formatMessage({ id: "BILLS.VOTE.ERROR" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savingVoteBillStateError]);

  useEffect(() => {
    if (teamInfoIsNotFilled !== null) {
      if (teamInfoIsNotFilled) {
        setModalShow(true);
      } else {
        handlePublishProposal();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamInfoIsNotFilled]);

  const handleCheckTeamInfo = () => {
    const teamId = localStorage.getItem("teamId");
    dispatch(fetchTeam(+teamId));
  };

  const handlePublishProposal = () => {
    if (publishableProposalId) {
      dispatch(publishProposal({ proposalId: publishableProposalId, userId }));
    }
  };

  const handleDeleteProposal = async () => {
    if (deletableProposalId) {
      await dispatch(deleteProposal(deletableProposalId));
      setConfirmModalShow(false);
    }
  };

  const handleSaveDocumentState = (object: Object) => {
    const billId = object["billId"];
    const documentId = object["documentId"];
    const payload = object["payload"];
    dispatch(saveDocumentState(billId, documentId, payload));
  };

  if (fetchingCurrentBillsInProgress) {
    return (
      <Spinner animation="grow" role="status">
        <span className="sr-only">
          <FormattedMessage id="ECOMMERCE.COMMON.LOADING" />
        </span>
      </Spinner>
    );
  }

  if (fetchingCurrentBillError)
    return <p>{fetchingCurrentBillError?.message}</p>;

  if (!currentBill) return null;

  const hasDocuments = currentBill.documents?.length > 0;
  const IS_GROUP_ADMIN = userHasPermissions(
    Permission.ADMINISTRATE_TEAM,
    currentTeam
  );

  return (
    <>
      <ModalLayout
        // @ts-ignore: Unreachable code error
        show={unAuthorized}
        hideCancelButton={true}
        onHide={() => setUnAuthorized(false)}
        title={intl.formatMessage({
          id: "SESSION_EXPIRED_MODAL.SESSION_EXPIRED",
        })}
      >
        {{
          footer: (
            <Link
              to="/auth/login"
              className="navi-item cursor-pointer p-3 color-white"
            >
              <Button className="btn btn-primary">
                <FormattedMessage id="SESSION_EXPIRED_MODAL.PLEASE_GO_TO_LOGIN"></FormattedMessage>
              </Button>
            </Link>
          ),
        }}
      </ModalLayout>
      <BillHeading bill={currentBill} />
      <Container fluid className="py-8 px-0">
        <Tab.Container defaultActiveKey="1">
          <Row>
            <Col xl={12}>
              <Tab.Content>
                {DISPLAY_CODE_CONTENT && currentBill?.id === 34 && (
                  <BillPopup />
                )}

                {hasDocuments ? (
                  <div>
                    <Tabs
                      defaultActiveKey={currentBill.documents?.[0].id}
                      className="mb-3"
                    >
                      {currentBill.documents.map((document) => (
                        <Tab
                          key={document.id}
                          eventKey={document.id}
                          title={document.name}
                        >
                          <BillToggle document={document} key={document.id}>
                            <div className={css.root}>
                              {DISPLAY_CODE_CONTENT && (
                                <>
                                  <BillShowCode
                                    doc={document}
                                    documentId={[document.id]}
                                    topDocument={currentBill?.topDocument}
                                    voteBtn={<BillVoting />}
                                    billHasPassedDeadline={
                                      billHasPassedDeadline
                                    }
                                  />

                                  {(isModerator || isAdmin) && (
                                    <BillExportToDocx
                                      title={document.name}
                                      elementId={"kt_content"}
                                    />
                                  )}
                                </>
                              )}

                              {(DISPLAY_LIKUMI_CONTENT ||
                                (DISPLAY_CODE_CONTENT &&
                                  !document?.contentJson)) && (
                                <div
                                  style={{
                                    display: DISPLAY_CODE_CONTENT ? "none" : "",
                                  }}
                                >
                                  <Tiptap
                                    doc={document}
                                    onSaveDocState={handleSaveDocumentState}
                                  />

                                  {DISPLAY_CODE_CONTENT &&
                                    setTimeout(
                                      () => window.location.reload(),
                                      3000
                                    )}
                                </div>
                              )}
                            </div>
                          </BillToggle>
                        </Tab>
                      ))}
                    </Tabs>
                  </div>
                ) : (
                  <p>
                    <FormattedMessage id="BILL.DOC_DOES_NOT_EXIST" />
                  </p>
                )}
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>

        {DISPLAY_LIKUMI_CONTENT && <BillVoting />}

        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        {savingProposalStateError?.message}
        <ToastContainer />
      </Container>

      {DISPLAY_LIKUMI_CONTENT && (
        <TeamEditModal
          show={modalShow && IS_GROUP_ADMIN}
          onHide={() => {
            setModalShow(false);
            dispatch(clearPublishableProposalId());
          }}
          team={currentTeam}
          handlePublishProposal={handlePublishProposal}
          backdrop="static"
          keyboard={false}
        />
      )}

      <ConfirmDeleteModal
        show={confirmModalShow}
        onHide={() => setConfirmModalShow(false)}
        handleDelete={handleDeleteProposal}
      />
    </>
  );
};

export default BillPage;
